import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormHelperText,
    Grid,
    IconButton, Skeleton, TextField,
    Typography
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import nextId from "react-id-generator";
import { InventoryIcon } from "../../components/InventoryIcon";
import { Config } from "../../config/config";
import { DividerWithText } from "../../utils/divider";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface State {
    showModal: boolean;
    rewards: any[];
    package: null | any;
}

interface Props {
    playerId: string;
    form: any;
    dataLoaded: boolean;
}

class MessagePlayerWrapped extends React.Component<Props, State> {
    rewardItems: any;
    shopPackages: any;

    constructor(props) {
        super(props);

        this.state = {
            showModal: false,
            rewards: [],
            package: null,
        };

        this.rewardItems = getGlobalVar("ITEMS").map(item => {
            const name = Helpers.getLocalizationName(item.name);

            return {
                ...item,
                name,
                firstLetter: item.type.replace("_", " ").toUpperCase(),
            };
        });

        this.shopPackages = getGlobalVar("SHOP_PACKAGES").map(pack => {
            const firstLetter = pack.type.replace("_", " ")[0].toUpperCase();
            const items = pack.items.map(item => ({
                ...item,
                name: this.rewardItems.find(reward => reward.id === item.itemId)?.name || "Undefined",
            }))

            return {
                ...pack,
                items,
                firstLetter: /[0-9]/.test(firstLetter) ? "0-9" : firstLetter,
            }
        });
    }

    showModal = () => {
        this.setState({ showModal: true });
    }

    hideModal = () => {
        this.setState({ showModal: false });
    }

    cancel = () => {
        this.setState({ showModal: false, rewards: [], package: null });
        this.props.form.resetFields();
    }

    addNewItem = () => {
        this.setState({
            rewards: [...this.state.rewards, {
                key: nextId(),
                value: "",
                amount: 0,
            }],
        });
    }

    changePackage = (value) => {
        this.setState({ package: value });
    }

    removeItem = (itemId) => {
        this.setState(prevState => ({
            rewards: prevState.rewards.filter(reward => reward.key !== itemId)
        }));
    }

    changeRewardItemAmount = (id: string, e: any) => {
        const tempVal = this.state.rewards;
        const element = tempVal.findIndex(reward => reward.key === id);

        tempVal[element].amount = +e.target.value;

        this.setState({ rewards: tempVal });
    }

    changeRewardItem = (id: string, value: string) => {
        const tempVal = this.state.rewards;
        const element = tempVal.findIndex(reward => reward.key === id);

        tempVal[element].value = value;

        this.setState({ rewards: tempVal });
    }

    send = () => {
        this.props.form.validateFields((_error, value) => {
            // Format rewards
            const rewards = this.state.package ? this.state.package.items.map(item => ({
                value: item.itemId,
                amount: item.amount,
            })) : this.state.rewards.map(item => ({
                ...item,
                value: item.value.id,
            })).filter(item => !!item.value);

            const data = {
                rewards,
                title: value.title,
                text: value.body,
                uid: this.props.playerId,
            };

            axios.post(`${Config.getInstance.apiUrl}/api/user/send_messages`, {data},
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                })
                .then(response => {
                    this.cancel();
                })
                .catch(() => {
                    PopupMessage.error("Something went wrong!");
                });
        });
    }

    renderMessageRewards = () => {
        if ((this.state.rewards || []).length === 0) {
            return <Typography variant="body1" gutterBottom>
                No items selected
            </Typography>
        }

        return (this.state.rewards || []).map(reward => (
            <div style={{display: "inline"}} key={reward.key}>
                <FormControl style={{
                    marginRight: 8,
                    width: "70%",
                    marginBottom: 10,
                }}>
                     <Autocomplete
                        onChange={(_, newValue) => this.changeRewardItem(reward.key, newValue)}
                        value={reward.item}
                        options={this.rewardItems.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
                        groupBy={option => option.firstLetter}
                        getOptionLabel={option => option.name}
                        renderInput={(params) => <TextField {...params} label="Reward" id={`reward_select_${reward.key}`} size="small"/>}
                    />
                </FormControl>

                <FormControl style={{
                    width: "20%",
                    marginBottom: 10,
                }}>
                    <TextField
                        id={`reward-title-input-${reward.key}`}
                        type="number"
                        value={reward.amount}
                        onChange={event => this.changeRewardItemAmount(reward.key, event)}
                        label="Amount"
                        size="small"
                    />
                </FormControl>

                {this.state.rewards.length >= 1 &&
                    <IconButton
                        aria-label="Delete"
                        style={{
                            marginLeft: 10
                        }}
                        onClick={() => this.removeItem(reward.key)}
                        size="medium"
                    >
                        <DeleteIcon fontSize="inherit"/>
                    </IconButton>
                }
            </div>
        ))
    }

    getItemName(itemId: string) {
        const item = getGlobalVar("ITEMS").find(itemObject => itemObject.id === itemId);
        return item ? Helpers.getLocalizationName(item.name) : itemId;
    }

    render() {
        const { getFieldDecorator, getFieldError } = this.props.form;

        if (!this.props.dataLoaded) {
            return <Skeleton width={110} height={55}/>
        }

        return (
            <div>
                <Button disableElevation variant="contained" size="small" onClick={this.showModal}>
                    Message Player
                </Button>

                <Dialog
                    open={this.state.showModal}
                    onClose={this.hideModal}
                    fullWidth={true}
                    maxWidth="md"
                >
                    <DialogContent dividers>
                        <FormControl
                            fullWidth={true}
                            required={true}
                            style={{ marginBottom: 30, marginTop: 20 }}
                            error={getFieldError("title")}
                        >
                            {getFieldDecorator("title",
                                {
                                    validateTrigger: "onBlur",
                                    rules: [{
                                        required: true,
                                        message: "Please enter title",
                                    }]
                                },
                            )(
                                <TextField
                                    size="small"
                                    label="Title"
                                />
                            )}

                            {getFieldError("title") ?
                                <FormHelperText>{getFieldError("title").join(",")}</FormHelperText>
                            : null}
                        </FormControl>

                        <FormControl
                            fullWidth={true}
                            required={true}
                            style={{ marginBottom: 30 }}
                            error={getFieldError("body")}
                        >
                            {getFieldDecorator("body",
                                {
                                    validateTrigger: "onBlur",
                                    rules: [{
                                        required: true,
                                        message: "Please enter body",
                                    }]
                                },
                            )(
                                <TextField
                                    multiline={true}
                                    rows="10"
                                    size="small"
                                    label="Body"
                                />
                            )}

                            {getFieldError("body") ?
                                <FormHelperText>{getFieldError("body").join(",")}</FormHelperText>
                            : null}
                        </FormControl>

                        <div style={{marginTop: 10}}>
                            {!this.state.package && <React.Fragment>
                                <Typography variant="h6" gutterBottom>
                                    Custom rewards
                                </Typography>

                                <div style={{ marginBottom: 20 }}>
                                    {this.renderMessageRewards()}
                                </div>

                                <Button disableElevation onClick={this.addNewItem} style={{width: "100%"}} variant="contained" color="primary">
                                    <AddIcon/> Add item
                                </Button>

                                <DividerWithText>OR</DividerWithText>
                            </React.Fragment>}

                            <Typography variant="h6" gutterBottom>
                                Package
                            </Typography>

                            <Autocomplete
                                onChange={(_, newValue) => this.changePackage(newValue)}
                                value={this.state.package}
                                options={this.shopPackages.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
                                getOptionLabel={option => option.id}
                                renderInput={(params) => <TextField {...params} label="Package" size="small"/>}
                            />

                            {this.state.package && <div style={{ marginTop: 20 }}>
                                <Grid container spacing={2} style={{ marginTop: 15, marginLeft: -8 }}>
                                    {this.state.package.items.map((reward, index) => (
                                        <Grid item sm={1} key={reward.item + index} style={{ marginRight: 15 }}>
                                            <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                                <div style={{ margin: "0 10px" }}>
                                                    {InventoryIcon({
                                                        itemId: reward.itemId,
                                                        style: { maxWidth: 70 },
                                                        amountStyle: { fontSize: 16 },
                                                        amount: reward.amount,
                                                    })}
                                                </div>
                                                <div style={{ marginTop: 5, textAlign: "center", fontSize: 12 }}>
                                                    {this.getItemName(reward.itemId)}
                                                </div>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>}
                        </div>

                    </DialogContent>

                    <DialogActions>
                        <Button disableElevation onClick={this.cancel} variant="contained">
                            Cancel
                        </Button>
                        <Button disableElevation onClick={this.send} color="primary" variant="contained">
                            Send
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

export const MessagePlayer = createForm()(MessagePlayerWrapped);
